import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollContainer, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';
import {
  listingKey, readListingState, captureListingState, restoreListingScroll, restoreListingLayout, mergeListingState,
} from '@theme/muuv-collection-history';

/**
 * Native server ordering/filtering, with cumulative infinite pages restored on Back.
 * History contains only the loaded page range, grid layout and visible product anchor.
 * Infinite loads replace the current entry; real filter/sort navigations remain entries.
 */
export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #pendingPages = new Map();
  #freshPages = new Set();
  #rendering = new Set();
  #generation = 0;
  #key = '';
  #url = '';
  #restoring = false;
  #userInteracted = false;
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    this.#key = listingKey(window.location.href);
    this.#url = window.location.href;
    this.setAttribute('data-muuv-listing-history', '');
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });

    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    document.addEventListener('muuv:before-history-push', this.#save);
    this.addEventListener('muuv:listing-leave', this.#save);
    window.addEventListener('pagehide', this.#save);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('popstate', this.#handlePopState);
    window.addEventListener('wheel', this.#markInteraction, { passive: true });
    window.addEventListener('touchstart', this.#markInteraction, { passive: true });
    window.addEventListener('keydown', this.#markInteraction);
    scrollContainerMediaQuery.addEventListener('change', this.#observeViewMore);
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.removeAttribute('data-muuv-listing-history');
    this.infinityScrollObserver?.disconnect();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    document.removeEventListener('muuv:before-history-push', this.#save);
    this.removeEventListener('muuv:listing-leave', this.#save);
    window.removeEventListener('pagehide', this.#save);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('wheel', this.#markInteraction);
    window.removeEventListener('touchstart', this.#markInteraction);
    window.removeEventListener('keydown', this.#markInteraction);
    scrollContainerMediaQuery.removeEventListener('change', this.#observeViewMore);
  }

  get #grid() { return this.querySelector('[ref="grid"]'); }
  get #cards() { return Array.from(this.#grid?.querySelectorAll(':scope > [ref="cards[]"]') ?? []); }
  get #lastPage() { return Number(this.#grid?.dataset.lastPage) || 0; }
  get #viewportTop() { return getIntersectionRoot()?.getBoundingClientRect().top ?? 0; }
  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #markInteraction = (event) => {
    if (event.type !== 'keydown' || ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', 'Tab', ' '].includes(event.key)) {
      this.#userInteracted = true;
    }
  };

  async #initialize() {
    const generation = this.#generation;
    const saved = readListingState(history.state, window.location.href, this.sectionId, this.#lastPage);
    this.#restoring = true;
    this.setAttribute('data-muuv-history-restoring', '');
    try {
      if (saved && this.getAttribute('infinite-scroll') !== 'false') {
        await this.#restoreRange(saved, generation);
      } else if (saved && this.#grid) {
        // A merchant may disable infinite loading between visits. Never inject
        // a formerly cumulative range into a now-native paginated page.
        restoreListingLayout(this.#grid, this, saved.layout, mediaQueryLarge.matches);
        restoreListingScroll(this.#grid, getScrollContainer(), saved, this.#viewportTop);
      }
    } catch {
      // A failed page request leaves the valid server-rendered current page usable.
      // No stale stored HTML or fabricated cards are substituted.
    } finally {
      if (generation !== this.#generation || !this.isConnected) return;
      this.#restoring = false;
      this.removeAttribute('data-muuv-history-restoring');
      this.#save();
      this.#observeViewMore();
      this.#prefetch();
    }
  }

  async #restoreRange(saved, generation) {
    const grid = this.#grid;
    if (!grid) return;
    const currentCards = this.#cards;
    const rebuiltCards = [];
    for (let page = saved.firstPage; page <= saved.lastPage; page++) {
      const present = currentCards.filter((card) => Number(card.dataset.page) === page);
      const cards = present.length ? present : await this.#fetchCards(page, true);
      if (!cards?.length || generation !== this.#generation || !this.isConnected) return;
      rebuiltCards.push(...cards);
    }
    if (listingKey(window.location.href) !== this.#key || generation !== this.#generation) return;
    grid.replaceChildren(...rebuiltCards);
    restoreListingLayout(grid, this, saved.layout, mediaQueryLarge.matches);
    this.#aspectRatioHelper?.processNewElements();
    // Two layout frames allow custom card elements and responsive ratios to settle.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (generation === this.#generation && !this.#userInteracted) {
      restoreListingScroll(grid, getScrollContainer(), saved, this.#viewportTop);
    }
  }

  #save = () => {
    const grid = this.#grid;
    if (!grid || this.#restoring || !this.isConnected || listingKey(window.location.href) !== this.#key) return;
    const saved = captureListingState(grid, getScrollContainer(), window.location.href, this.sectionId, this.#viewportTop);
    if (!saved) return;
    try { history.replaceState(mergeListingState(history.state, saved), ''); } catch { /* History may be unavailable. */ }
  };

  #handlePageShow = (event) => {
    if (!event.persisted || this.#restoring) return;
    const saved = readListingState(history.state, window.location.href, this.sectionId, this.#lastPage);
    if (saved && this.#grid) {
      requestAnimationFrame(() => restoreListingScroll(this.#grid, getScrollContainer(), saved, this.#viewportTop));
    }
  };

  #handlePopState = () => {
    if (window.location.href === this.#url) return;
    // Filter controls, counts and products must all match the historical URL.
    // A native reload of the SAME history entry is deliberate: it restores the
    // complete server-rendered section without an AJAX/old-filter race or an extra entry.
    window.location.reload();
  };

  #observeViewMore = () => {
    this.infinityScrollObserver?.disconnect();
    if (this.#restoring || this.getAttribute('infinite-scroll') === 'false') return;
    const previous = this.querySelector('[ref="viewMorePrevious"]');
    const next = this.querySelector('[ref="viewMoreNext"]');
    if (!previous && !next) return;
    this.infinityScrollObserver = new IntersectionObserver(async (entries) => {
      if (viewTransition.current) await viewTransition.current;
      for (const entry of entries) {
        if (!entry.isIntersecting || this.#restoring || !this.isConnected) continue;
        if (entry.target === previous) this.#renderPage('previous');
        if (entry.target === next) this.#renderPage('next');
      }
    }, { root: getIntersectionRoot(), rootMargin: '100px' });
    if (previous) this.infinityScrollObserver.observe(previous);
    if (next) this.infinityScrollObserver.observe(next);
  };

  #getPage(type) {
    const cards = this.#cards;
    const card = type === 'previous' ? cards[0] : cards.at(-1);
    if (!card) return null;
    const page = Number(card.dataset.page) + (type === 'previous' ? -1 : 1);
    return page >= 1 && page <= this.#lastPage ? page : null;
  }

  async #fetchCards(page, fresh = false) {
    const key = this.#key;
    const generation = this.#generation;
    const cacheKey = key + '|page=' + page;
    const requestFresh = fresh || this.#freshPages.has(page);
    let html = requestFresh ? null : this.pages.get(page);
    if (!html) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(page));
      url.hash = '';
      if (!this.#pendingPages.has(cacheKey)) {
        const request = sectionRenderer.getSectionHTML(this.sectionId, !requestFresh, url);
        this.#pendingPages.set(cacheKey, request);
      }
      const pending = this.#pendingPages.get(cacheKey);
      try { html = await pending; }
      finally { if (this.#pendingPages.get(cacheKey) === pending) this.#pendingPages.delete(cacheKey); }
      if (key !== this.#key || generation !== this.#generation || !this.isConnected) return null;
    }
    const document = new DOMParser().parseFromString(html, 'text/html');
    const grid = document.querySelector('[ref="grid"]');
    const cards = Array.from(grid?.querySelectorAll(':scope > [ref="cards[]"]') ?? []);
    // Reject an error/login response or a page that does not match the requested page.
    if (!cards.length || cards.some((card) => Number(card.dataset.page) !== page)) {
      this.pages.delete(page);
      // SectionRenderer may itself cache a successful HTTP error/login response.
      // Bypass that cache on the next attempt instead of poisoning all retries.
      this.#freshPages.add(page);
      return null;
    }
    this.pages.set(page, html);
    this.#freshPages.delete(page);
    return cards;
  }

  async #renderPage(type) {
    if (this.#restoring || this.#rendering.has(type)) return;
    const page = this.#getPage(type);
    if (page === null) return;
    const generation = this.#generation;
    this.#rendering.add(type);
    try {
      const cards = await this.#fetchCards(page);
      const grid = this.#grid;
      if (!cards || !grid || generation !== this.#generation || !this.isConnected || listingKey(window.location.href) !== this.#key) return;
      if (this.#cards.some((card) => Number(card.dataset.page) === page)) return;
      const anchor = captureListingState(grid, getScrollContainer(), window.location.href, this.sectionId, this.#viewportTop);
      if (type === 'previous') grid.prepend(...cards);
      else grid.append(...cards);
      this.#aspectRatioHelper?.processNewElements();
      if (type === 'previous' && anchor) restoreListingScroll(grid, getScrollContainer(), anchor, this.#viewportTop);
      await yieldToMainThread();
      if (generation !== this.#generation || !this.isConnected) return;
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(page));
      url.searchParams.delete('section_id');
      url.hash = '';
      // Autoload is one browsing context, not a succession of Back-button stops.
      history.replaceState(history.state, '', url.toString());
      this.#url = window.location.href;
      this.#save();
      requestIdleCallback(() => this.#prefetch());
    } catch {
      // Keep existing cards usable; a later observer intersection can retry.
    } finally { this.#rendering.delete(type); }
  }

  #prefetch() {
    if (this.#restoring || !this.isConnected || this.getAttribute('infinite-scroll') === 'false') return;
    for (const type of ['next', 'previous']) {
      const page = this.#getPage(type);
      if (page !== null) this.#fetchCards(page).catch(() => {});
    }
  }

  #handleFilterUpdate = async (event) => {
    const section = event.target?.closest?.('[id^="shopify-section-"]');
    // Header predictive events are outside this section and must never clear a
    // collection's cache/history. Only this section's actual facet event applies.
    if (!section || section.id !== 'shopify-section-' + this.sectionId) return;
    const generation = ++this.#generation;
    this.pages.clear();
    this.#pendingPages.clear();
    this.#freshPages.clear();
    this.#rendering.clear();
    this.infinityScrollObserver?.disconnect();
    this.#key = listingKey(window.location.href);
    this.#url = window.location.href;
    this.#restoring = true;
    try {
      if (event.promise) await event.promise;
      else if (viewTransition.current) await viewTransition.current;
    } catch {
      // Do not save old cards under the new filter URL if the section render failed.
      this.#generation++;
      window.location.reload();
      return;
    } finally {
      if (generation === this.#generation && this.isConnected) {
        this.#restoring = false;
        this.removeAttribute('data-muuv-history-restoring');
        this.#save();
        this.#observeViewMore();
        this.#prefetch();
      }
    }
  };
}
